import React, { useState, useCallback, useEffect } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  maxLength,
  number,
  regex,
  EditProps,
  SelectInput,
  useEditController,
  BooleanInput,
  FormDataConsumer,
  useDataProvider,
  useNotify,
} from "react-admin";
import { Grid, Typography, makeStyles } from "@material-ui/core";
import { LatLngInput } from "./SchoolCreate";
import CustomImageInput from "components/CustomImageInput";
import SanitizedGrid from "components/SanitizedGrid";
import CustomEditToolbar from "components/CustomEditToolbar";
import { emailValidation, mobilePhoneValidation } from "utils/validation";
import CustomOneMap from "./components/CustomOneMap";
import CustomAddressAutoCompleteInput from "./components/CustomAddressAutoCompleteInput";
import {
  areaCalculationType,
  ccaDropOffLocationType,
  ccaRateChartType,
  paymentMethodType,
  pricingPolicyList,
} from "./const";
import CcaReadmePdfInput from "./components/CcaReadmePdfInput";
import CcaEventPdfInput from "./components/CcaEventPdfInput";
import { requestPresignedUrl } from "./tools";
import CustomTooltip from "components/CustomTooltip";
import InfoIcon from "@material-ui/icons/Info";
import EditWrapper from "../../components/EditWrapper";
import PermissionLoading from "../../components/PermissionLoading";
import Alert from "@material-ui/lab/Alert";
import CustomNumberInput from "../../components/CustomNumberInput";

const useStyles = makeStyles({
  captionTooltip: {
    marginTop: "5px",
    marginBottom: "5px",
  },
  captionText: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "2px",
  },
  alertToggleCcaSplitPayment: {
    marginTop: "-35px",
    marginBottom: "25px",
  },
  alertToggleCcaSplitPaymentList: {
    paddingLeft: "15px",
    marginTop: "10px",
    marginBottom: "0px",
  },
  alertToggleCcaSplitPaymentListItem: {
    marginBottom: "5px",
  },
});

const SchoolEdit = (props: EditProps) => {
  const provider = useDataProvider();
  const notify = useNotify();
  const [lat, setlat] = useState<string | undefined>("");
  const [lng, setLng] = useState<string | undefined>("");
  const [uploadProgressCcaReadme, setUploadProgressCcaReadme] =
    useState<number>(0);
  const [uploadProgressCcaEvent, setUploadProgressCcaEvent] =
    useState<number>(0);
  const [isActiveCcaRateChartExist, setIsActiveCcaRateChartExist] =
    useState<boolean>(false);
  const classes = useStyles();
  const { record, loading } = useEditController(props);

  const formatRecord = (res: any) => {
    if (res) {
      if (res.isFullCalendarDaysCharge) {
        res.pricingPolicy = "fullCalendarDays";
      } else {
        res.pricingPolicy = "contactDays";
      }

      res.maxStudentSubsidy = res.maximumStudentSubsidy / 100;

      if (!res.subsidyDescriptionPrefix) {
        res.subsidyDescriptionPrefix = "Enabling Transport Subsidy";
      }

      return res;
    }
  };

  const initializeLocation = useCallback(
    ({ type, coordinate }: any) => {
      type === "lat" && setlat(Number(coordinate).toPrecision(8));
      type === "lng" && setLng(Number(coordinate).toPrecision(8));
    },
    [setlat, setLng],
  );

  const { id } = props;

  useEffect(() => {
    if (id) {
      provider
        .getList("ccaRateCharts", {
          filter: { SchoolId: Number(id), Status: "Active" },
          pagination: {
            page: 1,
            perPage: 1000,
          },
          sort: {
            field: "Id",
            order: "ASC",
          },
        })
        .then(({ data }) => {
          if (data && data.length > 0) {
            setIsActiveCcaRateChartExist(true);
          } else {
            setIsActiveCcaRateChartExist(false);
          }
        })
        .catch(() => {
          setIsActiveCcaRateChartExist(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const breadcrumb = [
    { link: "/schools", title: "Schools" },
    { link: `/schools/${id}`, title: id },
  ];

  const transform = async (data: any) => {
    if (data.photoUrl) {
      if (data.photoUrl.url) {
        data.photoUrl = (data.photoUrl.url as string).split(",")[1];

        delete data.photoUrlFile;
      }
    } else {
      data.photoUrl = null;
    }

    if (data.ccaReadmePdfFile) {
      try {
        const presignedUrl = await requestPresignedUrl(provider);

        const urlFilePath = await provider.uploadFileToAws(
          presignedUrl.pdfUrl,
          {
            data: data.ccaReadmePdfFile.rawFile,
            callback: (percent: number) => {
              setUploadProgressCcaReadme(percent);
            },
          },
        );

        data.ccaReadmePdfUrl = urlFilePath.data;
        delete data.ccaReadmePdfFile;
      } catch (error: any) {
        notify(`${error.message ?? error.toString()}`, "error");
      }
    }

    if (data.ccaEventPdfFile) {
      try {
        const presignedUrl = await requestPresignedUrl(provider);

        const urlFilePath = await provider.uploadFileToAws(
          presignedUrl.pdfUrl,
          {
            data: data.ccaEventPdfFile.rawFile,
            callback: (percent: number) => {
              setUploadProgressCcaEvent(percent);
            },
          },
        );

        data.ccaEventPdfUrl = urlFilePath.data;
        delete data.ccaEventPdfFile;
      } catch (error: any) {
        notify(`${error.message ?? error.toString()}`, "error");
      }
    }

    if (data.pricingPolicy === "contactDays") {
      data.isFullCalendarDaysCharge = false;
    } else {
      data.isFullCalendarDaysCharge = true;
    }

    if (data.ccaDropOffType === "door_to_door") {
      data.isAllowParentSelectWave = false;
    }

    if (!data.isCcaSettingEnable) {
      data.useDifferentCcaSplitPayment = false;
    }

    data.maximumStudentSubsidy = (Number(data.maxStudentSubsidy) * 100).toFixed(
      0,
    );

    if (!data.isStudentSubsidyEnable) {
      data.maximumStudentSubsidy = 0;
      data.subsidyDescriptionPrefix = null;
    }

    data.schoolPrefix = data.schoolPrefix.toUpperCase();

    delete data.pricingPolicy;

    return data;
  };

  const SchoolPrefixFormater = (data: string) => {
    if (data) {
      return data.toUpperCase();
    }
  };

  const callback = (value: { lat: string; lng: string }) => {
    setlat(value.lat);
    setLng(value.lng);
  };

  if (loading) {
    return <PermissionLoading />;
  }

  return (
    <>
      <EditWrapper breadcrumb={breadcrumb} transform={transform} {...props}>
        <SimpleForm
          record={formatRecord(record)}
          toolbar={<CustomEditToolbar isDeleteWithConfirm />}
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">School Information</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="School Name"
                source="name"
                validate={required()}
              />
            </Grid>

            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Email Address"
                source="email"
                type="email"
                validate={emailValidation}
              />
            </Grid>

            <Grid item xs={4}>
              <TextInput
                label="Contact Number"
                source="phone"
                // regular Expression for TextInput doesnt have white spaces
                validate={mobilePhoneValidation}
              />
            </Grid>

            <Grid item xs={4}></Grid>

            <Grid item xs={5}>
              <CustomImageInput
                label="Profile image"
                source="photoUrl"
                fieldType="image"
                accept="image/*"
                placeholder="Drop a images (*.jpg/jpeg/png) to upload, or click to select it."
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">School Address</Typography>
            </Grid>

            <Grid item xs={2}>
              <CustomAddressAutoCompleteInput
                label="Postal Code *"
                source="postalCode"
                referenceSource="locationData"
                parentCallback={callback}
              />
            </Grid>
            <Grid item xs={10}></Grid>

            <Grid item xs={2}>
              <TextInput
                label="Block No."
                source="blockNumber"
                validate={required()}
              />
            </Grid>
            <Grid item xs={10}></Grid>

            <Grid item xs={4}>
              <TextInput
                label="Address"
                source="address"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={1}>
              <TextInput label="Unit No." source="unitNumber" />
            </Grid>
            <Grid item xs={3}>
              <TextInput label="Building Name" source="building" />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">
                Map Location (Location for school's dropoff/pickup)
              </Typography>
            </Grid>

            <Grid item xs={6}>
              {lat !== "" && lng !== "" && (
                <CustomOneMap
                  center={{ lat, lng }}
                  referenceSource="locationData"
                />
              )}
              <LatLngInput
                lat={lat}
                lng={lng}
                updateLocation={initializeLocation}
              />
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Other Details</Typography>
            </Grid>

            <Grid item xs={3}>
              <TextInput
                label="School Prefix"
                source="schoolPrefix"
                format={SchoolPrefixFormater}
                validate={[required(), regex(/^[A-Za-z0-9]+$/), maxLength(5)]}
                placeholder="Enter Up to 5 Alphanumeric Characters"
              />
            </Grid>

            <Grid item xs={3}>
              <TextInput
                label="Pre VA and Beacon"
                source="virtualAccountPrefix"
                validate={[required(), number(), maxLength(2)]}
                placeholder="Enter Up to 2 Digits"
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={3}>
              <TextInput
                label="MBC Code"
                source="mbcCode"
                validate={[required()]}
              />
            </Grid>
            <Grid item xs={9}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="areaDistanceCalculationType"
                label="Type for Student Area Distance Calculation"
                choices={areaCalculationType}
                validate={required()}
              />
            </Grid>

            <Grid item xs={3}>
              <SelectInput
                source="subcontractorAreaDistanceCalculationType"
                label="Type for Subcontractor Area Distance Calculation"
                choices={areaCalculationType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={3}>
              <SelectInput
                source="pricingPolicy"
                label="Pricing Policy"
                choices={pricingPolicyList}
                validate={required()}
              />
            </Grid>

            <Grid item xs={3}>
              <SelectInput
                source="paymentMethodType"
                label="Type for Parent Payment Method"
                choices={paymentMethodType}
                validate={required()}
              />
            </Grid>
            <Grid item xs={6}></Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="isCustomPickupAndDropoffLocationAllowedForRouteplan"
                label="Allow Custom Pickup and Dropoff Location In Routeplan"
              />
              <Alert severity="info">
                <Typography variant="body2">
                  If checked, custom pickup and dropoff location (Change
                  Location and Move Marker) will be allowed in routeplan.
                </Typography>
              </Alert>
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Student Subsidy Settings</Typography>
            </Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="isStudentSubsidyEnable"
                label="Enable Student Subsidy"
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) =>
                formData.isStudentSubsidyEnable && (
                  <>
                    <Grid item xs={3} style={{ marginTop: "-35px" }}>
                      <CustomNumberInput
                        label="Max Bus Fare Cap ($)"
                        source="maxStudentSubsidy"
                        isRequired
                        isPositiveValue
                      />
                    </Grid>
                    <Grid item xs={9}></Grid>

                    <Grid item xs={6}>
                      <TextInput
                        multiline={true}
                        label="Subsidy Description Format"
                        source="subsidyDescriptionPrefix"
                        inputProps={{ maxLength: 35 }}
                        placeholder="Description is only up to 35 characters"
                        validate={[required(), maxLength(35)]}
                      />
                    </Grid>
                    <Grid item xs={6}></Grid>

                    <Grid item xs={6}>
                      <Alert
                        severity="info"
                        style={{ marginTop: "-15px", marginBottom: "25px" }}
                      >
                        <Typography variant="body2">
                          Description can be up to <strong>35 characters</strong> only.
                          <br />
                          <strong>Format:</strong> [Subsidy Description Format] [Rate Chart Name] - [School MBC Code] - [Student Name]
                          <br />
                          <strong>Example:</strong>{" "}
                          {`${formData.subsidyDescriptionPrefix || "Enabling Transport Subsidy"} November 2025 - ${(formData.mbcCode || "WTS").toUpperCase()} - John Doe`}
                        </Typography>
                      </Alert>
                    </Grid>
                    <Grid item xs={6}></Grid>
                  </>
                )
              }
            </FormDataConsumer>

            <Grid item xs={12}>
              <Typography variant="h6">CCA Settings</Typography>
            </Grid>

            <Grid item xs={6}>
              <BooleanInput
                source="isCcaSettingEnable"
                label="Enable CCA Setting"
                disabled={isActiveCcaRateChartExist}
              />
              {isActiveCcaRateChartExist && (
                <Alert
                  severity="info"
                  style={{ marginTop: "-20px", marginBottom: "25px" }}
                >
                  Can't disable CCA Setting because there are still active rate
                  charts.
                  <br />
                  Please delete those active rate charts to disable CCA Setting
                </Alert>
              )}
            </Grid>
            <Grid item xs={6}></Grid>

            <FormDataConsumer>
              {({ formData }) => (
                <>
                  {formData.isCcaSettingEnable && (
                    <SanitizedGrid
                      container
                      spacing={2}
                      style={{ marginTop: "-35px", padding: "10px" }}
                    >
                      <Grid item xs={3}>
                        <SelectInput
                          source="ccaDropOffType"
                          label="Type for CCA Drop-Off Location"
                          choices={ccaDropOffLocationType}
                          validate={required()}
                        />
                      </Grid>
                      <Grid item xs={3}>
                        <SelectInput
                          source="ccaRateChartType"
                          label="Type for CCA Rate Chart Calculation"
                          choices={ccaRateChartType}
                          validate={required()}
                          disabled={isActiveCcaRateChartExist}
                        />
                        {isActiveCcaRateChartExist && (
                          <CustomTooltip
                            title={
                              <>
                                <Typography variant="caption">
                                  Can't change CCA Rate Chart Calculation Type
                                  because there are still active rate charts.
                                  <br />
                                  Please delete those active rate charts to
                                  change CCA Rate Chart Calculation Type
                                </Typography>
                              </>
                            }
                            aria-label="Can't change CCA Rate Chart Calculation Type"
                            classes={{
                              tooltip: classes.captionTooltip,
                            }}
                            placement="bottom-start"
                          >
                            <div className={classes.captionText}>
                              <InfoIcon fontSize="small" color="secondary" />
                              <Typography
                                variant="caption"
                                style={{ cursor: "default" }}
                              >
                                Can't change CCA Rate Chart Calculation Type.
                              </Typography>
                            </div>
                          </CustomTooltip>
                        )}
                      </Grid>
                      <Grid item xs={6}></Grid>

                      {formData.ccaDropOffType === "fixed_location" ||
                      formData.ccaDropOffType === "both" ? (
                        <Grid item xs={12}>
                          <BooleanInput
                            source="isAllowParentSelectWave"
                            label="Allow Parent to Select Wave for CCA Fixed Location Drop-Off Type"
                          />
                        </Grid>
                      ) : (
                        <></>
                      )}

                      <Grid item xs={4}>
                        <BooleanInput
                          source="useDifferentCcaSplitPayment"
                          label="Use Different CCA Split Payment"
                        />
                      </Grid>
                      <Grid item xs={8}></Grid>

                      <Grid item xs={6}>
                        <Alert
                          severity="info"
                          className={classes.alertToggleCcaSplitPayment}
                        >
                          <Typography variant="body2">
                            This toggle enables or disables the{" "}
                            <strong>CCA Split Payment</strong> setting in the{" "}
                            <strong>Payment Setting Menu</strong> on the{" "}
                            <strong>Student Page</strong>.
                          </Typography>
                          <ul
                            className={classes.alertToggleCcaSplitPaymentList}
                          >
                            <li
                              className={
                                classes.alertToggleCcaSplitPaymentListItem
                              }
                            >
                              <strong>Toggle OFF</strong>: The{" "}
                              <strong>CCA Split Payment</strong> setting remains
                              hidden, and the CCA split payment percentages are
                              same as Regular split payment percentages.
                            </li>
                            <li
                              className={
                                classes.alertToggleCcaSplitPaymentListItem
                              }
                            >
                              <strong>Toggle ON</strong>: The{" "}
                              <strong>CCA Split Payment</strong> setting appears
                              in the Payment Setting Menu, allowing admin to
                              configure different CCA split payment percentage
                              for each student.
                            </li>
                          </ul>
                        </Alert>
                      </Grid>
                      <Grid item xs={6}></Grid>

                      <Grid item xs={6}>
                        <BooleanInput
                          source="isAllowManyCcaAyInOneRegularAy"
                          label="Allow Multiple CCA Academic Year In One Regular Academic Year"
                        />
                      </Grid>
                      <Grid item xs={6}></Grid>

                      <Grid item xs={6}>
                        <BooleanInput
                          source="isAllowParentUpdateScheduleWhenRenewal"
                          label="Allow Parent Update CCA Schedule When Confirm Renewal"
                        />
                      </Grid>
                      <Grid item xs={6}></Grid>

                      <Grid item xs={7}>
                        <CcaReadmePdfInput
                          uploadProgress={uploadProgressCcaReadme}
                          id={id}
                        />
                      </Grid>
                      <Grid item xs={5}></Grid>

                      <Grid item xs={7}>
                        <CcaEventPdfInput
                          uploadProgress={uploadProgressCcaEvent}
                          id={id}
                        />
                      </Grid>
                      <Grid item xs={5}></Grid>
                    </SanitizedGrid>
                  )}
                </>
              )}
            </FormDataConsumer>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default SchoolEdit;
